<template>
  <div class="page-container">
    <div v-if="loading" class="loading-container">
      <p>Cargando perfil...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <h2>Error al cargar el perfil</h2>
      <p>{{ error }}</p>
    </div>
    
    <div v-else class="profile-container">
      <!-- Header del perfil -->
      <header class="profile-header">
        <div class="profile-info">
          <img 
            :src="userProfile.fotoPerfil || defaultAvatar" 
            :alt="`Foto de perfil de ${userProfile.nickname}`" 
            class="profile-avatar" 
          />
          <div class="profile-details">
            <h1>{{ userProfile.nombre }}</h1>
            <h2>@{{ userProfile.nickname }}</h2>
            <p class="profile-bio">{{ userProfile.biografia || 'Sin biografía disponible' }}</p>
            <div class="profile-stats">
              <span class="stat">{{ posts.length }} posts</span>
            </div>
          </div>
        </div>
        
        <!-- Botones de acción solo si es el perfil propio -->
        <div v-if="isOwnProfile" class="profile-actions">
          <button @click="toggleEditMode" class="btn-edit">
            {{ editMode ? 'Cancelar' : 'Editar Perfil' }}
          </button>
        </div>
      </header>
      <!-- Formulario de edición de perfil y cropper -->
      <div v-if="editMode && isOwnProfile" class="edit-form">
        <h3>Editar Perfil</h3>
        <div class="input-group">
          <label for="editNombre">Nombre:</label>
          <input v-model="editData.nombre" id="editNombre" type="text" />
        </div>
        <div class="input-group">
          <label for="editBiografia">Biografía:</label>
          <textarea v-model="editData.biografia" id="editBiografia" rows="3"></textarea>
        </div>
        <div class="input-group">
          <label for="editFotoPerfil">Foto de perfil:</label>
          <input id="editFotoPerfil" type="file" accept="image/*" @change="onFileChange" />
        </div>
        <div v-if="selectedImage" class="cropper-preview">
          <div class="cropper-preview-wrapper">
            <cropper
              :src="selectedImage"
              :stencil-props="{ aspectRatio: 1 }"
              :autoZoom="true"
              @change="onCropChange"
              ref="cropperRef"
              class="cropper-img"
            />
            <div v-if="croppedImage" class="preview-overlay">
              <img :src="croppedImage" alt="Preview recorte" class="preview-img-circular" />
              <span class="preview-label">Vista previa</span>
            </div>
          </div>
          <span v-if="pfpUploadError" class="error-message">{{ pfpUploadError }}</span>
          <span v-if="pfpUploadSuccess" class="success-message">{{ pfpUploadSuccess }}</span>
        </div>
        <div class="edit-actions">
          <button @click="saveProfile" class="btn-save main-save" :disabled="savingProfile">
            {{ savingProfile ? 'Guardando...' : 'Guardar Cambios' }}
          </button>
          <button @click="toggleEditMode" class="btn-cancel">Cancelar</button>
        </div>
        <span v-if="editError" class="error-message">{{ editError }}</span>
        <span v-if="editSuccess" class="success-message">{{ editSuccess }}</span>
      </div>

      <!-- Sección de crear post (solo si es el perfil propio) -->
      <div v-if="isOwnProfile && !editMode" class="create-post-section">
        <h3>Crear nuevo post</h3>
        <CreatePost @postCreated="onPostCreated" />
      </div>

      <!-- Tabs para posts y comentarios -->
      <section class="posts-section">
        <div class="profile-tabs">
          <button :class="['tab-btn', activeTab === 'posts' ? 'active' : '']" @click="activeTab = 'posts'">Posts</button>
          <button :class="['tab-btn', activeTab === 'comments' ? 'active' : '']" @click="activeTab = 'comments'">Comentarios</button>
        </div>
        <div v-if="activeTab === 'posts'">
          <h3>Posts de {{ userProfile.nickname }}</h3>
          <div v-if="loadingPosts" class="loading-posts">
            <p>Cargando posts...</p>
          </div>
          <div v-else-if="mainPosts.length === 0" class="no-posts">
            <p v-if="isOwnProfile">Aún no has publicado ningún post. ¡Crea tu primer post arriba!</p>
            <p v-else>Este usuario no ha publicado ningún post aún.</p>
          </div>
          <div v-else class="posts-list">
            <PostCard 
              v-for="post in mainPosts" 
              :key="post.id" 
              :post="post"
              :show-comments="false"
              @postDeleted="onPostDeleted"
              @commentAdded="onCommentAdded"
            />
          </div>
        </div>
        <div v-else-if="activeTab === 'comments'">
          <h3>Comentarios de {{ userProfile.nickname }}</h3>
          <div v-if="loadingPosts" class="loading-posts">
            <p>Cargando comentarios...</p>
          </div>
          <div v-else-if="userCommentsList.length === 0" class="no-posts">
            <p v-if="isOwnProfile">Aún no has comentado en ningún post.</p>
            <p v-else>Este usuario no ha comentado en ningún post aún.</p>
          </div>
          <div v-else class="comments-list">
            <div v-for="item in userCommentsList" :key="item.comment.id" class="comment-post-pair">
              <PostCard :post="item.post" :show-comments="false" :show-actions="false" />
              <div class="user-comment-inline">
                <div class="comment-header">
                  <span class="comment-author">@{{ item.comment.usuarioNickname }}</span>
                  <span class="comment-date">{{ formatDate(item.comment.fechaCreacion) }}</span>
                </div>
                <p class="comment-content">{{ item.comment.contenido }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { useRoute, useRouter } from 'vue-router';
import { userService, postService } from '@/services/apiService';
import CreatePost from './CreatePost.vue';
import PostCard from './PostCard.vue';

export default {
  name: 'AppProfile',
  components: {
    CreatePost,
    PostCard,
    Cropper
  },
  setup() {

    // --- Declaración de variables reactivas al inicio ---
    const route = useRoute();
    const router = useRouter();
    const userProfile = ref({});
    const posts = ref([]);
    const loading = ref(true);
    const loadingPosts = ref(false);
    const error = ref('');
    const editMode = ref(false);
    const editData = ref({});
    const editError = ref('');
    const editSuccess = ref('');
    const savingProfile = ref(false);
    // Foto de perfil
    const selectedImage = ref(null);
    const croppedImage = ref(null);
    const cropperRef = ref(null);
    const uploadingPfp = ref(false);
    const pfpUploadError = ref('');
    const pfpUploadSuccess = ref('');
    const activeTab = ref('posts');

    // Obtener datos del usuario logueado
    // Determinar qué perfil mostrar


    // --- Computed principales ---
    const mainPosts = computed(() => posts.value.filter(p => !p.postPadreId));
    // 1. Filtrar todos los comentarios hechos por el usuario
    // 2. De esos comentarios, obtener los posts padre
    // 3. Devolver pares {post, comment}
    const userCommentsList = computed(() => {
      // Todos los comentarios hechos por el usuario
      const userComments = posts.value.filter(p => p.postPadreId && p.usuarioNickname === profileNickname.value);
      // IDs únicos de posts padre
      const parentIds = [...new Set(userComments.map(c => c.postPadreId))];
      // Buscar los posts padre en la lista de posts
      const parentPosts = posts.value.filter(p => parentIds.includes(p.id));
      // Mapear cada comentario a su post padre
      return userComments.map(comment => {
        const parent = parentPosts.find(p => p.id === comment.postPadreId) || {
          id: comment.postPadreId,
          contenido: '[Post original no disponible]',
          usuarioNickname: '',
          fechaCreacion: '',
        };
        return { post: parent, comment };
      });
    });

    // Manejar recorte
    const onCropChange = () => {
      if (cropperRef.value) {
        const canvas = cropperRef.value.getResult().canvas;
        if (canvas) {
          croppedImage.value = canvas.toDataURL('image/jpeg');
        }
      }
    };

    // Subir imagen recortada al backend
    const uploadCroppedImage = async () => {
      if (!croppedImage.value) return;
      uploadingPfp.value = true;
      pfpUploadError.value = '';
      pfpUploadSuccess.value = '';
      try {
        // Convertir base64 a Blob
        const blob = await (await fetch(croppedImage.value)).blob();
        const formData = new FormData();
        formData.append('file', blob, 'pfp.jpg');
        // Llamada al endpoint backend
        const response = await fetch(`http://api.gogosaporu.com/usuarios/${profileNickname.value}/foto-perfil`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` },
          body: formData
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Error al subir la foto');
        // Actualizar en pantalla
        userProfile.value.fotoPerfil = data.fotoPerfil;
        editData.value.fotoPerfil = data.fotoPerfil;
        pfpUploadSuccess.value = 'Foto de perfil actualizada';
        selectedImage.value = null;
        croppedImage.value = null;
      } catch (err) {
        pfpUploadError.value = err.message;
      } finally {
        uploadingPfp.value = false;
      }
    };
    
    const defaultAvatar = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSlbUvJQpkNm-m4qOhZPVS5oqHD6gZIdfv2BA&s';
    
    // Obtener datos del usuario logueado
    const currentUserNickname = localStorage.getItem('userNickname');
    const authToken = localStorage.getItem('authToken');
    
    // Determinar qué perfil mostrar
    // Soporta rutas /profile y /profile/:nickname
    const profileNickname = computed(() => {
      // Si la ruta es exactamente /profile o /profile/ (propio)
      if (route.path === '/profile' || route.path === '/profile/') {
        return currentUserNickname;
      }
      // Si la ruta es /profile/:nickname y existe el param
      if (route.params.nickname) {
        return route.params.nickname;
      }
      // Fallback: propio
      return currentUserNickname;
    });
    
    const isOwnProfile = computed(() => {
      return profileNickname.value === currentUserNickname;
    });
    
    const loadUserProfile = async () => {
      try {
        loading.value = true;
        error.value = '';
        
        if (!authToken) {
          router.push('/forbidden');
          return;
        }
        
        const response = await userService.getUserProfile(profileNickname.value, authToken);
        userProfile.value = response;
        
        // Inicializar datos de edición
        editData.value = {
          nombre: response.nombre,
          biografia: response.biografia || '',
          fotoPerfil: response.fotoPerfil || ''
        };
        
      } catch (err) {
        console.error('Error loading user profile:', err);
        error.value = err.message;
        
        if (err.message.includes('404') || err.message.includes('not found')) {
          router.push('/404');
        }
      } finally {
        loading.value = false;
      }
    };

    const loadUserPosts = async () => {
      try {
        loadingPosts.value = true;
        const response = await postService.getUserPosts(profileNickname.value, authToken);
        posts.value = response;
      } catch (err) {
        console.error('Error loading user posts:', err);
        // No mostrar error para posts, solo logs
      } finally {
        loadingPosts.value = false;
      }
    };
    
    const toggleEditMode = () => {
      editMode.value = !editMode.value;
      editError.value = '';
      editSuccess.value = '';
      
      if (editMode.value) {
        // Resetear datos de edición
        editData.value = {
          nombre: userProfile.value.nombre,
          biografia: userProfile.value.biografia || '',
          fotoPerfil: userProfile.value.fotoPerfil || ''
        };
      }
    };
    
    const saveProfile = async () => {
      try {
        savingProfile.value = true;
        editError.value = '';
        editSuccess.value = '';

        // Si hay imagen recortada, primero subirla
        if (croppedImage.value) {
          uploadingPfp.value = true;
          try {
            const blob = await (await fetch(croppedImage.value)).blob();
            const formData = new FormData();
            formData.append('file', blob, 'pfp.jpg');
            const response = await fetch(`https://api.gogosaporu.com:8080/usuarios/${profileNickname.value}/foto-perfil`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${authToken}` },
              body: formData
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al subir la foto');
            // Actualizar en pantalla y en datos a guardar
            userProfile.value.fotoPerfil = data.fotoPerfil;
            editData.value.fotoPerfil = data.fotoPerfil;
            selectedImage.value = null;
            croppedImage.value = null;
          } catch (err) {
            pfpUploadError.value = err.message;
            throw err;
          } finally {
            uploadingPfp.value = false;
          }
        }

        await userService.updateProfile(
          currentUserNickname,
          editData.value,
          authToken
        );

        // Actualizar el perfil mostrado
        userProfile.value = { ...userProfile.value, ...editData.value };
        editSuccess.value = 'Perfil actualizado exitosamente';

        setTimeout(() => {
          editMode.value = false;
          editSuccess.value = '';
        }, 2000);

      } catch (err) {
        console.error('Error updating profile:', err);
        editError.value = err.message;
      } finally {
        savingProfile.value = false;
      }
    };
    
    const editPost = (post) => {
      // TODO: Implementar edición de posts
      console.log('Editar post:', post);
    };
    
    const deletePost = async (postId) => {
      if (!confirm('¿Estás seguro de que quieres eliminar este post?')) {
        return;
      }
      
      try {
        await postService.deletePost(postId, currentUserNickname, authToken);
        // Recargar posts
        await loadUserPosts();
      } catch (err) {
        console.error('Error deleting post:', err);
        alert('Error al eliminar el post: ' + err.message);
      }
    };
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const onPostCreated = (newPost) => {
      // Agregar el nuevo post al inicio de la lista
      posts.value.unshift(newPost);
    };

    const onPostDeleted = (postId) => {
      // Eliminar el post de la lista
      posts.value = posts.value.filter(post => post.id !== postId);
    };

    const onCommentAdded = (parentPostId, newComment) => {
      // Actualizar el post padre si está en la lista
      const parentPost = posts.value.find(post => post.id === parentPostId);
      if (parentPost) {
        if (!parentPost.comentarios) {
          parentPost.comentarios = [];
        }
        parentPost.comentarios.push(newComment);
      }
    };

    // Manejar cambio de archivo de imagen de perfil
    const onFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          selectedImage.value = e.target.result;
          croppedImage.value = null; // Reinicia el recorte si se selecciona una nueva imagen
        };
        reader.readAsDataURL(file);
      }
    };
    
    onMounted(async () => {
      await loadUserProfile();
      await loadUserPosts();
    });
    
    return {
      userProfile,
      posts,
      loading,
      loadingPosts,
      error,
      editMode,
      editData,
      editError,
      editSuccess,
      savingProfile,
      defaultAvatar,
      isOwnProfile,
      toggleEditMode,
      saveProfile,
      editPost,
      deletePost,
      formatDate,
      onPostCreated,
      onPostDeleted,
      onCommentAdded,
      // Foto de perfil
      selectedImage,
      croppedImage,
      cropperRef,
      uploadingPfp,
      pfpUploadError,
      pfpUploadSuccess,
      onFileChange,
      onCropChange,
      uploadCroppedImage,
      profileNickname,
      authToken,
      activeTab,
      userCommentsList,
      mainPosts
    };
  }
};
</script>

<style scoped>
/* --- Cropper y preview UX mejoras --- */
.cropper-preview {
  margin-bottom: 2rem;
}
.cropper-preview-wrapper {
  position: relative;
  width: 70%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
.cropper-img {
  width: 100% !important;
  max-width: 100%;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
.preview-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
}
.preview-img-circular {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  background: #f8f9fa;
}
.preview-label {
  margin-top: 0.5rem;
  color: #6a0dad;
  font-size: 0.95rem;
  font-weight: 600;
  background: #fff;
  border-radius: 12px;
  padding: 0.2rem 0.8rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.main-save {
  font-size: 1.1rem;
  padding: 0.9rem 2.2rem;
  background: #6a0dad;
  color: #fff;
  border-radius: 8px;
  margin-right: 1rem;
  box-shadow: 0 2px 8px rgba(106,13,173,0.08);
  border: none;
  font-weight: bold;
  transition: background 0.2s;
}
.main-save:disabled {
  background: #ccc;
  color: #fff;
  cursor: not-allowed;
}
/* Tabs de perfil */
.profile-tabs {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.tab-btn {
  background: #f8f9fa;
  color: #6a0dad;
  border: 1px solid #e1e8ed;
  border-radius: 20px 20px 0 0;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.tab-btn.active {
  background: #6a0dad;
  color: white;
  border-bottom: 2px solid #6a0dad;
}

.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.loading-container, .error-container {
  text-align: center;
  padding: 2rem;
}

.profile-container {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 2rem;
  background: linear-gradient(135deg, #6a0dad, #8a2be2);
  color: white;
}

.profile-info {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
}

.profile-details h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
}

.profile-details h2 {
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  opacity: 0.8;
}

.profile-bio {
  margin: 0 0 1rem 0;
  max-width: 400px;
  line-height: 1.4;
}

.profile-stats {
  display: flex;
  gap: 1rem;
}

.stat {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
}

.profile-actions {
  display: flex;
  gap: 1rem;
}

.btn-edit {
  background: white;
  color: #6a0dad;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}

.btn-edit:hover {
  background: #f0f0f0;
  transform: translateY(-2px);
}

.edit-form {
  padding: 2rem;
  border-bottom: 1px solid #eee;
}

.edit-form h3 {
  margin: 0 0 1.5rem 0;
  color: #333;
}

.input-group {
  margin-bottom: 1rem;
}

.input-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #333;
}

.input-group input,
.input-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
}

.input-group input:focus,
.input-group textarea:focus {
  outline: none;
  border-color: #6a0dad;
}

.edit-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: center;
  align-items: center;
}

.btn-save {
  background: #6a0dad;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
}

.btn-save:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-cancel {
  background: #ccc;
  color: #333;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 5px;
  cursor: pointer;
}

.create-post-section {
  padding: 2rem;
  border-bottom: 1px solid #e1e8ed;
  background: #f8f9fa;
}

.create-post-section h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
}

.posts-section {
  padding: 2rem;
}

.posts-section h3 {
  margin: 0 0 1.5rem 0;
  color: #333;
}

.loading-posts, .no-posts {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.posts-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.post-card {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1.5rem;
  background: white;
}

.post-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.post-date {
  color: #666;
  font-size: 0.9rem;
}

.post-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-edit-post, .btn-delete-post {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
}

.btn-edit-post {
  background: #6a0dad;
  color: white;
}

.btn-delete-post {
  background: #dc3545;
  color: white;
}

.post-content p {
  margin: 0 0 1rem 0;
  line-height: 1.6;
}

.post-media {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.post-image, .post-video {
  width: 100%;
  max-width: 100%;
  border-radius: 8px;
}

.post-video {
  max-height: 300px;
}

.error-message {
  color: red;
  font-size: 0.9rem;
  margin-top: 1rem;
  display: block;
}

.success-message {
  color: green;
  font-size: 0.9rem;
  margin-top: 1rem;
  display: block;
}

@media (max-width: 768px) {
  .profile-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .profile-info {
    flex-direction: column;
    text-align: center;
  }
  
  .profile-avatar {
    width: 100px;
    height: 100px;
  }
  
  .page-container {
    padding: 1rem;
  }
}
</style>
  